import {useCallback, useState} from 'react';
import {capitalize} from 'lodash';
import Image from 'next/image';

import {PaymentMethodType} from '#apollo/schema';
import {useSendPaymentMethodUpdateLinkMutation} from '#apollo/schema';
import {Icon, Text} from '#component/base';
import {useToastQueue} from '#hook';

import {
  infoBox,
  innerContent,
  innerContentEditText,
  innerContentEditTrigger,
  innerContentEditWrapper,
  innerContentHeading,
  innerContentHeadingText,
  innerContentText,
} from '../../InfoBoxes.css';
import {EditPaymentConfirmationModal} from './components/EditPaymentConfirmationModal';
import {EditPaymentLoadingModal} from './components/EditPaymentLoadingModal';

import {PaymentMethodInfoBox} from './PaymentInfoBox.types';

const PaymentInfoBox = ({paymentMethod}: {paymentMethod: PaymentMethodInfoBox}) => {
  const {addToast} = useToastQueue();
  const [sendPaymentMethodUpdateLinkMutation, {loading: sendingUpdateEmail}] =
    useSendPaymentMethodUpdateLinkMutation();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const handleSendUpdatePaymentLink = useCallback(async () => {
    try {
      const {data, errors} = await sendPaymentMethodUpdateLinkMutation();
      if (data?.sendPaymentMethodUpdateLink.success) {
        setConfirmationModalOpen(true);
        return;
      }
      throw new Error(
        `Failed to send payment method update link: ${
          errors ? errors.join(', ') : 'Unknown error'
        }`,
      );
    } catch (error) {
      addToast({
        children: 'Uh oh, something went wrong!',
      });
      throw error;
    }
  }, [addToast, sendPaymentMethodUpdateLinkMutation]);

  return (
    <>
      <div className={infoBox}>
        <div className={innerContentHeading}>
          <Text as="h3" variant="body" className={innerContentHeadingText}>
            Payment Method
          </Text>
          <div className={innerContentEditWrapper}>
            <button onClick={handleSendUpdatePaymentLink} className={innerContentEditTrigger}>
              <Text as="p" variant="body" className={innerContentEditText}>
                Edit
              </Text>
              <Icon
                name="PencilSimple"
                size="small"
                color="burgundy"
                className={innerContentEditText}
              />
            </button>
          </div>
        </div>

        <div className={innerContent}>
          <Text as="h3" variant="body" className={innerContentText}>
            {paymentMethod.type === PaymentMethodType.Creditcard &&
              `${capitalize(paymentMethod.brand || 'Card')} ending in ${paymentMethod.lastDigits}`}
            {paymentMethod.type === PaymentMethodType.Paypal && paymentMethod.brand}
            {paymentMethod.type === PaymentMethodType.Shoppay &&
              `Shop Pay card ending in ${paymentMethod.lastDigits}`}
          </Text>
          <Text as="p" variant="body" className={innerContentText}>
            {(paymentMethod.type === PaymentMethodType.Creditcard ||
              paymentMethod.type === PaymentMethodType.Shoppay) &&
              `Expires ${paymentMethod.expiryMonth}/${
                paymentMethod?.expiryYear?.toString().slice(-2) ?? ''
              }`}
          </Text>
          <picture>
            {((paymentMethod.brand && paymentMethod.type !== PaymentMethodType.Shoppay) ||
              paymentMethod.type === PaymentMethodType.Shoppay) && (
              <Image
                src={`/image/profile/payment-method/${
                  paymentMethod.type === PaymentMethodType.Shoppay
                    ? 'shoppay'
                    : paymentMethod.brand!.toLowerCase()
                }.png`}
                alt={`${
                  paymentMethod.type === PaymentMethodType.Shoppay
                    ? 'Shop Pay'
                    : paymentMethod.brand
                }`}
                width={64}
                height={40}
              />
            )}
          </picture>
        </div>
      </div>
      {sendingUpdateEmail && <EditPaymentLoadingModal />}
      <EditPaymentConfirmationModal
        paymentMethodType={paymentMethod.type}
        open={confirmationModalOpen}
        onOpenChange={setConfirmationModalOpen}
      />
    </>
  );
};

export default PaymentInfoBox;
